import { latLngToSvgPiecewise } from "../../utils/gps/projectionPiecewise.js";
import { subscribeDeviceHeading } from "../../utils/gps/deviceOrientation.js";
import { focusOnPointAnimated } from "../../utils/viewerHelpers.js";
import { getMapNorthOffsetDeg } from "../../utils/campusHelpers.js";
import { pickNearestCampus } from "../../utils/map/findNearestCampus";
import {
  isInNativeApp,
  subscribeNativeGps,
  subscribeNativeHeading,
} from "../../utils/nativeBridge";
import { USE_GPS } from "../../../config/gpsConfig";
import {
  USE_MOCK_GPS,
  MOCK_LOCATION,
  MOCK_HEADING_DEG,
} from "../../../config/gpsDebugConfig";
import {
  GPS_SMOOTHING_ALPHA,
  GPS_DRAW_INTERVAL_MS,
} from "../../../constants/gpsParams";
import { METERS_PER_DEGREE_LATITUDE } from "../../../constants/geodesyConstants";
import { gpsActions } from "../reducers/mapPanZoomReducer.js";
import campusData from "../../../json/campus_data.json";
import campusConfig from "../../../json/campus_config.json";

/**
 * GPS関連のミュータブルな内部状態を保持するストアを生成
 * @param {number | null} initialCampus 初期キャンパスID
 * @returns {{
 *   transformModels: Record<number, object | null>,
 *   prevSvgPos: Record<number, {x: number, y: number} | null>,
 *   unsubscribeHeading: (() => void) | null,
 *   gpsWatchId: number | null,
 *   lastDraw: number,
 *   smoothPos: {lat: number, lng: number} | null,
 *   unsubscribeNativeGps: (() => void) | null,
 *   unsubscribeNativeHeading: (() => void) | null,
 *   pendingCenterOnGps: boolean,
 *   runtimeCampus: number | null
 * }} GPS管理用ストア
 */
export function createGpsStore(initialCampus = null) {
  return {
    transformModels: {},
    prevSvgPos: {},
    unsubscribeHeading: null,
    gpsWatchId: null,
    lastDraw: 0,
    smoothPos: null,
    unsubscribeNativeGps: null,
    unsubscribeNativeHeading: null,
    pendingCenterOnGps: false,
    runtimeCampus: initialCampus,
  };
}

/**
 * コンパス監視を開始する
 * @param {object} gpsStoreRef GPSストアref
 * @param {function} dispatch 統合dispatch
 * @param {number} currentCampus 現在のキャンパス
 * @param {string} gpsEnabled GPS有効状態
 */
function startCompass(gpsStoreRef, dispatch, currentCampus, gpsEnabled) {
  const gpsStore = gpsStoreRef.current;

  if (gpsStore.unsubscribeHeading) gpsStore.unsubscribeHeading();

  if (gpsEnabled !== "granted" && gpsEnabled !== "retry") return;

  // ネイティブアプリ内の場合
  if (isInNativeApp()) {
    gpsStore.unsubscribeNativeHeading = subscribeNativeHeading((data) => {
      if (data.heading !== null && data.heading !== undefined) {
        // 購読コールバック内ではgpsStoreから最新キャンパスを取得
        const campus = gpsStore.runtimeCampus ?? currentCampus;
        const offset = getMapNorthOffsetDeg(campusData, campus);
        const degToShow = (((data.heading + offset) % 360) + 360) % 360;
        dispatch(gpsActions.setHeading(degToShow));
      }
    });
    return;
  }

  // ブラウザで直接開いた場合
  gpsStore.unsubscribeHeading = subscribeDeviceHeading((deg) => {
    // 購読コールバック内ではgpsStoreから最新キャンパスを取得
    const campus = gpsStore.runtimeCampus ?? currentCampus;
    const offset = getMapNorthOffsetDeg(campusData, campus);
    const degToShow = (((deg + offset) % 360) + 360) % 360;
    dispatch(gpsActions.setHeading(degToShow));
  });
}

/**
 * SVG座標へ変換してGPSマーカーを描画
 * @param {object} gpsStoreRef GPSストアref
 * @param {object} svgRootRef SVGルートref
 * @param {function} dispatch 統合dispatch
 * @param {number} lat 緯度
 * @param {number} lng 経度
 * @param {number | null} accuracy 精度
 */
function drawGpsMarker(gpsStoreRef, svgRootRef, dispatch, lat, lng, accuracy) {
  const gpsStore = gpsStoreRef.current;
  const svgRoot = svgRootRef.current;
  // 購読コールバック内ではgpsStoreから最新キャンパスを取得
  const currentCampus = gpsStore.runtimeCampus;

  if (!USE_GPS) return;

  const model = gpsStore.transformModels[currentCampus];
  if (!svgRoot || !model) return;

  const svgPt = latLngToSvgPiecewise(lat, lng, model);
  if (!svgPt) {
    dispatch(gpsActions.setMarker(false, null, null, 0));
    return;
  }

  // 微小移動はスキップ
  const prev = gpsStore.prevSvgPos[currentCampus];
  const microMove =
    prev && Math.hypot(svgPt.x - prev.x, svgPt.y - prev.y) < 0.8;

  if (!prev || !microMove) {
    gpsStore.prevSvgPos[currentCampus] = svgPt;

    let accuracyRadius = 0;
    if (accuracy != null && accuracy > 0) {
      const offsetLat = lat + accuracy / METERS_PER_DEGREE_LATITUDE;
      const accuracyEdgeSvgPt = latLngToSvgPiecewise(offsetLat, lng, model);
      if (accuracyEdgeSvgPt) {
        accuracyRadius = Math.abs(accuracyEdgeSvgPt.y - svgPt.y);
        if (accuracyRadius > 150) accuracyRadius = 0;
      }
    }

    dispatch(gpsActions.setMarker(true, svgPt.x, svgPt.y, accuracyRadius));
  }
}

/**
 * GPS値更新：EMA平滑化＋20fpsに間引きして描画
 * @param {object} gpsStoreRef GPSストアref
 * @param {object} svgRootRef SVGルートref
 * @param {function} dispatch 統合dispatch
 * @param {number} lat 緯度
 * @param {number} lng 経度
 * @param {number | null} accuracy 精度
 */
function updateGps(gpsStoreRef, svgRootRef, dispatch, lat, lng, accuracy) {
  const gpsStore = gpsStoreRef.current;

  if (!USE_GPS) return;

  const now = performance.now?.() ?? Date.now();
  if (now - gpsStore.lastDraw < GPS_DRAW_INTERVAL_MS) {
    dispatch(gpsActions.setCoords({ lat, lng, accuracy }));
    return;
  }

  gpsStore.lastDraw = now;
  gpsStore.smoothPos = gpsStore.smoothPos
    ? {
        lat:
          gpsStore.smoothPos.lat +
          GPS_SMOOTHING_ALPHA * (lat - gpsStore.smoothPos.lat),
        lng:
          gpsStore.smoothPos.lng +
          GPS_SMOOTHING_ALPHA * (lng - gpsStore.smoothPos.lng),
      }
    : { lat, lng };

  dispatch(gpsActions.setCoords({ lat, lng, accuracy }));
  drawGpsMarker(
    gpsStoreRef,
    svgRootRef,
    dispatch,
    gpsStore.smoothPos.lat,
    gpsStore.smoothPos.lng,
    accuracy,
  );
}

/**
 * 最寄りキャンパスをチェックしてSnackbarを表示
 * @param {object} gpsStoreRef GPSストアref
 * @param {function} dispatch 統合dispatch
 * @param {number} lat 緯度
 * @param {number} lng 経度
 */
function checkNearestCampus(gpsStoreRef, dispatch, lat, lng) {
  const gpsStore = gpsStoreRef.current;
  const { runtimeCampus } = gpsStore;

  const nearest = pickNearestCampus(lat, lng, campusData);
  const nearestCampusId = Number.isInteger(nearest) ? nearest : null;

  if (nearestCampusId && nearestCampusId !== runtimeCampus) {
    dispatch(gpsActions.setNearestCampus(nearestCampusId));
    dispatch(gpsActions.setSnackbar(true));
  } else {
    dispatch(gpsActions.setNearestCampus(nearestCampusId));
  }
}

/**
 * 現在地のGPSにフォーカスする
 * @param {object} gpsStoreRef GPSストアref
 * @param {object} viewerRef ビューアref
 * @param {object} svgRootRef SVGルートref
 * @param {object} gpsSlice nearestCampus, markerVisible, markerX, markerY
 * @param {object} mapSlice currentCampus, currentCampusCode
 * @param {string} options.gpsEnabled GPS有効状態
 * @param {function} options.handleCampusChange キャンパス変更ハンドラ
 */
export function centerOnCurrentGps(
  gpsStoreRef,
  viewerRef,
  svgRootRef,
  gpsSlice,
  mapSlice,
  options,
) {
  const gpsStore = gpsStoreRef.current;
  const viewer = viewerRef.current;
  const svgRoot = svgRootRef.current;

  const { gpsEnabled, handleCampusChange } = options;

  const { nearestCampus, markerVisible, markerX, markerY } = gpsSlice;
  const { currentCampus, currentCampusCode } = mapSlice;

  if (!USE_GPS || (gpsEnabled !== "granted" && gpsEnabled !== "retry")) return;

  if (nearestCampus > 0 && nearestCampus !== currentCampus) {
    gpsStore.pendingCenterOnGps = true;
    handleCampusChange(nearestCampus);
    return;
  }

  if (!markerVisible) return;

  focusOnPointAnimated(
    viewer,
    svgRoot,
    markerX,
    markerY,
    campusConfig.currentLocationZoom[currentCampusCode],
    800,
  );
}

/**
 * マウント時のGPS初期化
 * @param {object} gpsStoreRef GPSストアref
 * @param {object} svgRootRef SVGルートref
 * @param {function} dispatch 統合dispatch
 * @param {string} options.gpsEnabled GPS有効状態
 * @param {function} options.updateGpsEnabled GPS有効状態更新関数
 * @param {number} options.initialCampus 初期キャンパス
 */
export function initGpsOnMount(gpsStoreRef, svgRootRef, dispatch, options) {
  const gpsStore = gpsStoreRef.current;

  const { gpsEnabled, updateGpsEnabled, initialCampus } = options;

  if (!USE_GPS || (gpsEnabled !== "granted" && gpsEnabled !== "retry")) return;

  startCompass(gpsStoreRef, dispatch, initialCampus, gpsEnabled);

  if (USE_MOCK_GPS) {
    updateGps(
      gpsStoreRef,
      svgRootRef,
      dispatch,
      MOCK_LOCATION.lat,
      MOCK_LOCATION.lng,
      MOCK_LOCATION.acc,
    );
    checkNearestCampus(gpsStoreRef, dispatch, MOCK_LOCATION.lat, MOCK_LOCATION.lng);
    return;
  }

  // ネイティブアプリ内の場合
  if (isInNativeApp()) {
    let isFirstGps = true;
    gpsStore.unsubscribeNativeGps = subscribeNativeGps((data) => {
      const { lat, lng, accuracy } = data;
      if (lat === null || lng === null) return;

      if (isFirstGps) {
        isFirstGps = false;
        checkNearestCampus(gpsStoreRef, dispatch, lat, lng);
      }

      updateGps(gpsStoreRef, svgRootRef, dispatch, lat, lng, accuracy);
      if (gpsEnabled === "retry" && updateGpsEnabled) {
        updateGpsEnabled("granted");
      }
    });
    return;
  }

  // ブラウザで直接開いた場合
  if (!navigator.geolocation) {
    console.warn("[GPS not available] Skipping watchPosition");
    return;
  }

  try {
    let isFirstGps = true;
    gpsStore.gpsWatchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;

        if (isFirstGps) {
          isFirstGps = false;
          checkNearestCampus(gpsStoreRef, dispatch, latitude, longitude);
        }

        updateGps(
          gpsStoreRef,
          svgRootRef,
          dispatch,
          latitude,
          longitude,
          accuracy,
        );

        if (gpsEnabled === "retry" && updateGpsEnabled) {
          updateGpsEnabled("granted");
        }
      },
      (err) => {
        console.error("[GPS watch error]", err);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 2000 },
    );
  } catch (error) {
    console.error("[GPS watchPosition exception]", error);
  }
}

/**
 * アンマウント時のGPSクリーンアップ
 * @param {object} gpsStore GPSストア
 */
export function cleanupGps(gpsStore) {
  if (USE_GPS && gpsStore.gpsWatchId !== null && navigator.geolocation) {
    navigator.geolocation.clearWatch(gpsStore.gpsWatchId);
  }

  if (gpsStore.unsubscribeHeading) gpsStore.unsubscribeHeading();
  if (gpsStore.unsubscribeNativeGps) gpsStore.unsubscribeNativeGps();
  if (gpsStore.unsubscribeNativeHeading) gpsStore.unsubscribeNativeHeading();
}

/**
 * SVG Ready時のGPS描画初期化
 * @param {object} gpsStoreRef GPSストアref
 * @param {object} svgRootRef SVGルートref
 * @param {function} dispatch 統合dispatch
 * @param {number} currentCampus 現在のキャンパス
 * @param {number | null} gpsAccuracy GPS精度
 */
export function initGpsOnSvgReady(
  gpsStoreRef,
  svgRootRef,
  dispatch,
  currentCampus,
  gpsAccuracy,
) {
  const gpsStore = gpsStoreRef.current;

  if (!USE_GPS) return;

  const model = gpsStore.transformModels[currentCampus];
  if (!model) return;

  if (USE_MOCK_GPS) {
    drawGpsMarker(
      gpsStoreRef,
      svgRootRef,
      dispatch,
      MOCK_LOCATION.lat,
      MOCK_LOCATION.lng,
      MOCK_LOCATION.acc,
    );
    dispatch(
      gpsActions.setHeading(
        MOCK_HEADING_DEG + getMapNorthOffsetDeg(campusData, currentCampus),
      ),
    );
  } else if (
    gpsStore.smoothPos?.lat != null &&
    gpsStore.smoothPos?.lng != null
  ) {
    drawGpsMarker(
      gpsStoreRef,
      svgRootRef,
      dispatch,
      gpsStore.smoothPos.lat,
      gpsStore.smoothPos.lng,
      gpsAccuracy ?? null,
    );
  }
}